import { XMLParser, type X2jOptions } from 'fast-xml-parser'

/**
 * 預設的 XML 解析選項
 */
const defaultParserOptions: Partial<X2jOptions> = {
  ignoreAttributes: false,
  parseTagValue: false
  // 不設定 isArray，讓 fast-xml-parser 自然處理
  // 多個相同標籤 → 自動解析為陣列
  // 單個標籤 → 解析為物件
}

/**
 * 解析 XML 格式的 fetch 回應
 *
 * fast-xml-parser 會自動處理：
 * - 多個相同標籤 → 自動解析為陣列
 * - 單個標籤 → 解析為物件
 *
 * @param response - fetch Response 物件
 * @param options - XMLParser 選項（可選）
 * @returns 解析後的 JSON 物件
 *
 * @example 基本使用
 * ```typescript
 * const response = await fetch('https://api.example.com/data.xml')
 * const data = await parseXmlResponse<MyDataType>(response)
 * ```
 *
 * @example 自訂解析選項
 * ```typescript
 * const data = await parseXmlResponse<MyDataType>(response, {
 *   ignoreAttributes: true,  // 忽略 XML 屬性
 *   parseTagValue: true      // 自動轉換數值
 * })
 * ```
 */
export async function parseXmlResponse<T = any>(
  response: Response,
  options?: Partial<X2jOptions>
): Promise<T> {
  if (!response.ok) {
    throw new Error(`Failed to fetch XML: ${response.statusText}`)
  }

  const xmlText = await response.text()
  const parser = new XMLParser({
    ...defaultParserOptions,
    ...options
  })

  return parser.parse(xmlText) as T
}
